import { scaleLinear, scaleTime } from "@visx/scale";
import { extent, max, min } from "d3-array";
import type { YFormatType } from "../context/ChartContext";
import { formatComponentValue } from "./formatting";

export type { YFormatType };

export interface BaseDataPoint {
  xValue: Date;
  yValue: number;
  category?: string;
}

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const defaultMargin: ChartMargin = {
  top: 0,
  right: 0,
  bottom: 50,
  left: 60,
};

export interface YFormatSettings {
  format: (value: number) => string;
  min?: number;
  max?: number;
  tickInterval?: number;
  tickFormat?: (value: number) => string;
}

/**
 * Get Y-axis format settings from a ComponentFormatter
 * Uses the main formatting system for consistency across all components
 */
export function getYFormatSettings(formatter: YFormatType = { type: "number" }): YFormatSettings {
  const safeFormat = (value: number | null | undefined): string => {
    if (value == null) return "-";
    const result = formatComponentValue({ value, formatter });
    // formatComponentValue returns ReactNode, convert to string
    if (result === null) return "-";
    if (typeof result === "string") return result;
    if (typeof result === "number") return result.toString();
    // For React elements, try to extract text content if possible
    // This handles the case where formatComponentValue returns JSX with units
    if (typeof result === "object" && result !== null && "props" in result) {
      // Extract text from React element children
      const extractText = (node: unknown): string => {
        if (node == null) return "";
        if (typeof node === "string") return node;
        if (typeof node === "number") return node.toString();
        if (typeof node === "object" && "props" in node) {
          const props = (node as { props?: { children?: unknown } }).props;
          if (props?.children) {
            if (Array.isArray(props.children)) {
              return props.children.map(extractText).join("");
            }
            return extractText(props.children);
          }
        }
        return "";
      };
      const text = extractText(result);
      return text || value.toString();
    }
    // Fallback to raw value for chart axes
    return value.toString();
  };

  // Create a simplified tick format for axis labels (less verbose)
  const tickFormat = (value: number | null | undefined): string => {
    if (value == null) return "-";

    // Handle string shorthand formatters
    if (typeof formatter === "string") {
      const result = formatComponentValue({ value, formatter });
      if (result === null) return "-";
      return String(result);
    }

    // For specific format types, provide concise axis labels
    if (typeof formatter === "object" && formatter.type) {
      switch (formatter.type) {
        case "energy":
        case "power":
        case "voltage":
        case "current":
        case "resistance":
        case "temperature":
        case "distance":
          // These formatters include units, which is good for axes
          return safeFormat(value);

        case "currency":
          // For currency, just use the standard format
          return safeFormat(value);

        default:
          return safeFormat(value);
      }
    }

    return safeFormat(value);
  };

  // Determine min/max/intervals based on format type
  let min: number | undefined;
  let max: number | undefined;
  let tickInterval: number | undefined;

  if (typeof formatter === "object" && formatter.type) {
    switch (formatter.type) {
      case "number":
        if ("min" in formatter) min = formatter.min as number | undefined;
        if ("max" in formatter) max = formatter.max as number | undefined;
        break;
    }
  }

  return {
    format: safeFormat,
    tickFormat,
    min,
    max,
    tickInterval,
  };
}

export const createXScale = (data: BaseDataPoint[], width: number) => {
  const dateExtent = extent(data, (d) => d.xValue) as [Date, Date];
  return scaleTime({
    range: [defaultMargin.left, width - defaultMargin.right],
    domain: dateExtent,
    nice: true,
  });
};

export const createYScale = (data: BaseDataPoint[], height: number, formatType: YFormatType) => {
  const yFormatSettings = getYFormatSettings(formatType);
  const dataYMin = min(data, (d) => d.yValue) || 0;
  const dataYMax = max(data, (d) => d.yValue) || 0;
  const yMin =
    yFormatSettings.min !== undefined
      ? yFormatSettings.min
      : dataYMin >= 0
        ? 0 // 0 if all positive values, min is 0
        : dataYMin * 1.1; // else scale the negative values by 10% to make room for the y-axis values
  const yMax =
    yFormatSettings.max !== undefined
      ? yFormatSettings.max
      : dataYMax <= 0
        ? 0 // If all values are negative, max is 0
        : dataYMax * 1.1; // else scale the positive values by 10% to make room for the y-axis values
  return scaleLinear({
    range: [height - defaultMargin.bottom, defaultMargin.top],
    domain: [yMin, yMax],
    nice: true,
  });
};
